import { useState, useMemo } from "react";
import { Calendar } from "./ui/calendar";
import { Trash2, Plus } from "lucide-react";

/**
 * Admin: editable list of unavailable dates for a talent.
 * Users click dates on a calendar to toggle them; chips below show the saved ones.
 */
export default function AvailabilityEditor({ value = [], onChange }) {
  const [pickerOpen, setPickerOpen] = useState(true);
  const dates = useMemo(() => Array.from(new Set(value.map((d) => d.slice(0, 10)))).sort(), [value]);
  const dateSet = new Set(dates);

  const toggle = (d) => {
    const iso = d.toISOString().slice(0, 10);
    const next = dateSet.has(iso) ? dates.filter((x) => x !== iso) : [...dates, iso].sort();
    onChange(next);
  };

  const remove = (iso) => onChange(dates.filter((d) => d !== iso));

  const today = new Date(); today.setHours(0, 0, 0, 0);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="label-overline text-zinc-500">{dates.length} date{dates.length === 1 ? "" : "s"} marked unavailable</span>
        <button type="button" onClick={() => setPickerOpen(!pickerOpen)} className="text-xs text-zinc-700 hover:text-black inline-flex items-center gap-1">
          <Plus size={12}/> {pickerOpen ? "Hide picker" : "Add dates"}
        </button>
      </div>

      {pickerOpen && (
        <div className="border border-black/15 bg-white p-3 inline-block">
          <Calendar
            mode="single"
            onSelect={(d) => d && toggle(d)}
            modifiers={{ unavailable: (d) => dateSet.has(d.toISOString().slice(0, 10)) }}
            modifiersClassNames={{ unavailable: "bg-rose-100 text-rose-900 font-medium" }}
            disabled={(d) => d < today}
            className="rounded-none"
          />
          <p className="text-xs text-zinc-500 mt-2 max-w-xs">Click any date to toggle. Highlighted dates are unavailable.</p>
        </div>
      )}

      {dates.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {dates.map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => remove(d)}
              className="text-xs px-2 py-1 border border-black/15 bg-white hover:bg-rose-50 hover:border-rose-300 inline-flex items-center gap-1 transition-colors"
              title="Click to remove"
            >
              {new Date(d).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })}
              <Trash2 size={11}/>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
